import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { Subscription, switchMap } from 'rxjs';
import {
  ImportElementType,
  ImportQuestion,
  MAX_IMPORT_FILE_BYTES,
  ProjectImport,
} from '../../../models/classic-import.model';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { ClassicImportService } from '../../../services/classic-import.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { FileDropzoneComponent } from '../../../components/file-dropzone/file-dropzone.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { ClassicImportReportComponent } from '../classic-import-report/classic-import-report.component';
import { ClassicImportTreeComponent } from '../classic-import-tree/classic-import-tree.component';
import { ClassicImportUsersComponent } from '../classic-import-users/classic-import-users.component';
import { ClassicImportConfirmComponent } from '../classic-import-confirm/classic-import-confirm.component';
import { saveBlob } from '../../../shared/file-download';
import { serverMessage } from '../../../shared/http/server-message';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';

type ClassicImportStep = 1 | 2 | 3 | 4;

const STEPS: readonly { step: ClassicImportStep; key: string }[] = [
  { step: 1, key: 'file' },
  { step: 2, key: 'selection' },
  { step: 3, key: 'users' },
  { step: 4, key: 'confirm' },
];
const SELECTION_STEP: ClassicImportStep = 2;
const USERS_STEP: ClassicImportStep = 3;
const LAST_STEP: ClassicImportStep = 4;
const ACCEPTED_FILE_TYPES = '.tox';
const REFRESH_DEBOUNCE_MS = 300;
const DONE_TYPES: readonly ImportElementType[] = [
  'TEST_CASE',
  'TEST_MODULE',
  'DATA_SHEET',
  'OBJECT_LIBRARY',
  'TEST_PLAN',
  'REQUIREMENT',
  'DOCUMENT',
];

/** The commit failed and nothing is in the project; `failure` comes from the report (422). */
interface CommitFailure {
  failure: string | null;
  detailKey: string;
}

/** Questions of the confirm step; USER_MAPPING is answered on the users step. */
function isConfirmQuestion(q: ImportQuestion): boolean {
  return q.type === 'ENCRYPT_PLAINTEXT' || q.type === 'EXISTING_ELEMENT';
}

/**
 * Classic-Import in den Projekt-Einstellungen: .tox hochladen, Probelauf-Bericht lesen, danach
 * Auswahl, Nutzer und Bestätigen. Eigenes Overlay nach dem Muster `test-case-import-modal`, weil
 * `lib-modal` den breiten Dialog mit Schrittanzeige nicht trägt.
 */
@Component({
  selector: 'app-classic-import-dialog',
  imports: [
    A11yModule,
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    FileDropzoneComponent,
    SectionTitleComponent,
    ClassicImportReportComponent,
    ClassicImportTreeComponent,
    ClassicImportUsersComponent,
    ClassicImportConfirmComponent,
    RouterLink,
  ],
  templateUrl: './classic-import-dialog.component.html',
  styleUrl: './classic-import-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassicImportDialogComponent {
  private readonly importService = inject(ClassicImportService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly visible = model(false);
  readonly imported = output<ProjectImport>();

  readonly steps = STEPS;
  readonly lastStep = LAST_STEP;
  readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;

  readonly step = signal<ClassicImportStep>(1);
  readonly importState = signal<ProjectImport | null>(null);
  readonly busy = signal(false);
  readonly errorKey = signal<string | null>(null);
  readonly errorText = signal<string | null>(null);
  /** Letzte Ausgabe des Baums; `null` heißt „alles“. Geht erst mit „Weiter“ an das BE. */
  readonly pendingSelection = signal<string[] | null>(null);
  /** Antworten des Nutzers über alle Schritte; an das BE gehen nur die zu Fragen des aktuellen Berichts. */
  readonly answers = signal<Record<string, string>>({});
  readonly members = signal<ProjectMemberDTO[] | null>(null);
  readonly membersLoading = signal(false);
  readonly membersFailed = signal(false);
  readonly committing = signal(false);
  readonly refreshing = signal(false);
  readonly commitFailure = signal<CommitFailure | null>(null);
  /** After 409 "already committed", „Importieren“ stays disabled even if the reload fails. */
  readonly alreadyCommitted = signal(false);

  readonly hasUserQuestions = computed(
    () => this.importState()?.report.questions.some((q) => q.type === 'USER_MAPPING') ?? false,
  );
  readonly usersSkipped = computed(() => this.importState() !== null && !this.hasUserQuestions());

  private readonly unblocked = computed(() => {
    const state = this.importState();
    return !this.busy() && state !== null && !state.report.blocked;
  });

  private readonly selectionChanged = computed(() => {
    const state = this.importState();
    return state !== null && !sameSelection(this.pendingSelection(), state.selection);
  });

  private readonly canLeaveSelection = computed(() => {
    const state = this.importState();
    if (this.busy() || state === null) return false;
    const pending = this.pendingSelection();
    const count = pending === null ? state.tree.length : pending.length;
    return count > 0 && (this.selectionChanged() || !state.report.blocked);
  });

  // Mit gespeicherter Teilauswahl führt ein Sperrgrund zurück in den Baum, damit der Nutzer die Auswahl ändern kann.
  private readonly canLeaveFile = computed(() => {
    const state = this.importState();
    return !this.busy() && state !== null && (!state.report.blocked || state.selection !== null);
  });

  readonly canProceed = computed(() => {
    const step = this.step();
    if (step === 1) return this.canLeaveFile();
    if (step === SELECTION_STEP) return this.canLeaveSelection();
    return this.unblocked() && step < LAST_STEP;
  });
  readonly hasOpenQuestion = computed(() => {
    const answers = this.answers();
    return (this.importState()?.report.questions ?? []).some(
      (q) => isConfirmQuestion(q) && !q.options.includes(answers[q.id]),
    );
  });
  readonly done = computed(() => this.importState()?.status === 'COMMITTED');
  /** Types of the end state; a type without created, replaced or skipped elements stays out. */
  readonly doneTypes = computed(() => {
    const counts = this.importState()?.report.counts ?? {};
    return DONE_TYPES.filter((type) => {
      const count = counts[type];
      return (count?.created ?? 0) + (count?.replaced ?? 0) + (count?.skipped ?? 0) > 0;
    });
  });
  readonly canCommit = computed(
    () =>
      this.step() === LAST_STEP &&
      this.unblocked() &&
      !this.hasOpenQuestion() &&
      !this.refreshing() &&
      !this.committing() &&
      !this.alreadyCommitted() &&
      !this.done(),
  );
  readonly commitHintKey = computed(() => {
    if (this.step() !== LAST_STEP || this.done()) return null;
    if (this.importState()?.report.blocked) return 'classicImport.confirm.blocked';
    return this.hasOpenQuestion() ? 'classicImport.confirm.openQuestions' : null;
  });

  private upload: Subscription | null = null;
  private refresh: Subscription | null = null;
  private commitRun: Subscription | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private triggerElement: HTMLElement | null = null;

  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        this.triggerElement = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
      } else if (!open && wasVisible) {
        document.body.style.overflow = '';
        this.triggerElement?.focus();
        this.triggerElement = null;
      }
      wasVisible = open;
    });
    this.destroyRef.onDestroy(() => {
      document.body.style.overflow = '';
      this.cancelRefreshTimer();
    });
  }

  onFileSelected(file: File): void {
    this.discardImport();
    if (!file.name.toLowerCase().endsWith(ACCEPTED_FILE_TYPES)) {
      this.errorKey.set('classicImport.errors.fileType');
      return;
    }
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      this.errorKey.set('classicImport.errors.tooLarge');
      return;
    }
    this.busy.set(true);
    this.upload = this.importService
      .upload(this.projectId(), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.importState.set(state);
          this.pendingSelection.set(state.selection);
          this.answers.set(state.answers ?? {});
          this.busy.set(false);
        },
        error: (err: unknown) => {
          this.showUploadError(err);
          this.busy.set(false);
        },
      });
  }

  onFileCleared(): void {
    this.discardImport();
  }

  next(): void {
    if (!this.canProceed()) return;
    if (this.step() === SELECTION_STEP && this.selectionChanged()) {
      this.sendSelection();
      return;
    }
    if (this.step() === USERS_STEP) {
      this.sendAnswers();
      return;
    }
    this.goTo((this.step() + 1) as ClassicImportStep, 1);
  }

  back(): void {
    if (this.busy() || this.committing() || this.step() === 1) return;
    this.goTo((this.step() - 1) as ClassicImportStep, -1);
  }

  loadMembers(): void {
    this.membersLoading.set(true);
    this.membersFailed.set(false);
    this.memberService
      .getMembers(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (members) => {
          this.members.set(members);
          this.membersLoading.set(false);
        },
        error: () => {
          this.membersFailed.set(true);
          this.membersLoading.set(false);
        },
      });
  }

  downloadReport(): void {
    const state = this.importState();
    if (!state) return;
    this.clearError();
    this.importService
      .downloadReport(this.projectId(), state.id, 'md')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (download) => saveBlob(download.blob, download.filename),
        error: () => this.errorKey.set('classicImport.errors.download'),
      });
  }

  // Radio clicks in quick succession lead to one dry run; „Importieren“ stays disabled until it answers.
  onConfirmAnswers(answers: Record<string, string>): void {
    if (this.committing()) return;
    this.answers.set(this.withoutStaleSheetAnswers(answers));
    this.refresh?.unsubscribe();
    this.refreshing.set(true);
    this.cancelRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refreshCounts();
    }, REFRESH_DEBOUNCE_MS);
  }

  /**
   * The content hash of a sheet contains its secret flags, so a changed encrypt answer adds or removes the question
   * EXISTING_ELEMENT of that sheet. The BE rejects an answer to a question it no longer asks.
   */
  private withoutStaleSheetAnswers(answers: Record<string, string>): Record<string, string> {
    const previous = this.answers();
    const next = { ...answers };
    for (const q of this.importState()?.report.questions ?? []) {
      if (q.type === 'ENCRYPT_PLAINTEXT' && previous[q.id] !== next[q.id]) delete next[`EXISTING_ELEMENT:${q.element}`];
    }
    return next;
  }

  private cancelRefreshTimer(): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  // `committing` is set synchronously before any request, so a second click finds „Importieren“ disabled.
  commit(): void {
    if (!this.canCommit()) return;
    const state = this.importState()!;
    this.committing.set(true);
    this.commitFailure.set(null);
    this.clearError();
    let commitSent = false;
    this.commitRun = this.importService
      .update(this.projectId(), state.id, { selection: state.selection, answers: this.answersToSend(state) })
      .pipe(
        switchMap((updated) => {
          this.importState.set(updated);
          commitSent = true;
          return this.importService.commit(this.projectId(), updated.id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.importState.set(result);
          this.committing.set(false);
          this.imported.emit(result);
        },
        error: (err: unknown) => {
          this.committing.set(false);
          // Without an answer to the commit, e.g. 504 after a proxy timeout, the BE may still be importing.
          if (commitSent && isUnclear(err)) this.reloadAfterUnclearCommit();
          // The PUT answers like the commit: 409 when already committed (e.g. a lost commit response), 403 on rights.
          else this.showCommitError(err);
        },
      });
  }

  // Escape aus einer offenen Auswahlliste (etwa dem Typfilter) schließt nur die Liste, nicht den Dialog samt Analyse.
  onEscape(event: Event): void {
    const origin = event.target instanceof Element ? event.target : null;
    if (event.defaultPrevented || origin?.closest('[role="combobox"][aria-expanded="true"]')) return;
    this.close();
  }

  close(): void {
    if (this.busy() || this.committing()) return;
    this.visible.set(false);
    this.discardImport();
  }

  // Antworten gehen leer mit: Mit der Auswahl ändern sich die Fragen, und das BE lehnt Antworten auf unbekannte ab.
  private sendSelection(): void {
    const state = this.importState()!;
    this.clearError();
    this.busy.set(true);
    this.importService
      .update(this.projectId(), state.id, { selection: this.pendingSelection(), answers: {} })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.importState.set(updated);
          this.pendingSelection.set(updated.selection);
          this.busy.set(false);
          if (!updated.report.blocked) this.goTo(USERS_STEP, 1);
        },
        error: (err: unknown) => {
          const text = err instanceof HttpErrorResponse && err.status === 400 ? serverMessage(err) : null;
          if (text) this.errorText.set(text);
          else this.errorKey.set('classicImport.errors.selection');
          this.busy.set(false);
        },
      });
  }

  private sendAnswers(): void {
    const state = this.importState()!;
    const answers = this.answersToSend(state);
    this.clearError();
    this.busy.set(true);
    this.importService
      .update(this.projectId(), state.id, { selection: state.selection, answers })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.importState.set(updated);
          this.busy.set(false);
          if (!updated.report.blocked) this.goTo(LAST_STEP, 1);
        },
        error: () => {
          this.errorKey.set('classicImport.users.errors.save');
          this.busy.set(false);
        },
      });
  }

  /** Sends what the user sees: only answers to questions of this report, persons only from the loaded members. */
  private answersToSend(state: ProjectImport): Record<string, string> {
    const questions = state.report.questions;
    const asked = new Set(questions.map((q) => q.id));
    const userMappings = new Set(questions.filter((q) => q.type === 'USER_MAPPING').map((q) => q.id));
    const persons = new Set(reviewerOptionsFromMembers(this.members() ?? []).map((o) => o.id));
    // A person outside the loaded members is shown as „nicht zuordnen“ in the rows.
    return Object.fromEntries(
      Object.entries(this.answers()).filter(
        ([id, value]) => asked.has(id) && (!userMappings.has(id) || persons.has(value)),
      ),
    );
  }

  // A new PUT cancels the previous one so the counts match the latest choice.
  private refreshCounts(): void {
    const state = this.importState()!;
    this.refresh?.unsubscribe();
    this.clearError();
    this.refreshing.set(true);
    this.refresh = this.importService
      .update(this.projectId(), state.id, { selection: state.selection, answers: this.answersToSend(state) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.importState.set(updated);
          this.refreshing.set(false);
        },
        error: (err: unknown) => {
          this.errorKey.set('classicImport.errors.refresh');
          this.refreshing.set(false);
          if (isBadRequest(err)) this.reloadSavedAnswers();
        },
      });
  }

  private showCommitError(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const body: unknown = err instanceof HttpErrorResponse ? err.error : null;
    const returned = body && typeof body === 'object' && 'report' in body ? (body as ProjectImport) : null;
    if (status === 409 && returned) {
      this.importState.set(returned);
      this.errorKey.set('classicImport.errors.commitBlocked');
    } else if (status === 409) {
      this.alreadyCommitted.set(true);
      const text = serverMessage(err);
      if (text) this.errorText.set(text);
      else this.errorKey.set('classicImport.errors.alreadyCommitted');
      this.reload();
    } else if (status === 422 && returned) {
      this.importState.set(returned);
      this.commitFailure.set({ failure: returned.report.failure, detailKey: 'classicImport.errors.commit' });
    } else if (status === 410) {
      // The BE deleted the file (discarded or expired); its text asks for a new upload.
      const text = body && typeof body === 'object' ? (body as { error?: unknown }).error : null;
      if (typeof text === 'string' && text) this.errorText.set(text);
      else this.errorKey.set('classicImport.errors.commit');
    } else if (status === 403) {
      this.commitFailure.set({ failure: null, detailKey: 'classicImport.errors.commitForbidden' });
    } else if (status === 400) {
      this.commitFailure.set({ failure: null, detailKey: 'classicImport.errors.commit' });
      this.reloadSavedAnswers();
    } else {
      this.commitFailure.set({ failure: null, detailKey: 'classicImport.errors.commit' });
    }
  }

  private reload(): void {
    const state = this.importState()!;
    this.importService
      .get(this.projectId(), state.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (reloaded) => this.importState.set(reloaded), error: () => undefined });
  }

  /** After a rejected PUT the dialog falls back to the saved state, so the next PUT does not fail the same way. */
  private reloadSavedAnswers(): void {
    const state = this.importState()!;
    this.importService
      .get(this.projectId(), state.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (reloaded) => {
          this.importState.set(reloaded);
          this.answers.set({ ...(reloaded.answers ?? {}) });
          this.enterConfirm();
        },
        error: () => undefined,
      });
  }

  private reloadAfterUnclearCommit(): void {
    const state = this.importState()!;
    this.refreshing.set(true);
    this.importService
      .get(this.projectId(), state.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (reloaded) => {
          this.importState.set(reloaded);
          this.refreshing.set(false);
          if (reloaded.status === 'COMMITTED') this.imported.emit(reloaded);
          else if (reloaded.status === 'FAILED')
            this.commitFailure.set({ failure: reloaded.report.failure, detailKey: 'classicImport.errors.commit' });
          else this.errorKey.set('classicImport.errors.commitUnclear');
        },
        error: () => {
          this.refreshing.set(false);
          this.errorKey.set('classicImport.errors.commitUnclearNoReload');
        },
      });
  }

  /** Ohne Frage USER_MAPPING fällt der Schritt „Nutzer“ in beide Richtungen weg. */
  private goTo(target: ClassicImportStep, direction: 1 | -1): void {
    const step = target === USERS_STEP && !this.hasUserQuestions() ? target + direction : target;
    this.step.set(step as ClassicImportStep);
    this.commitFailure.set(null);
    if (step === USERS_STEP && this.members() === null && !this.membersLoading()) this.loadMembers();
    if (step === LAST_STEP) this.enterConfirm();
  }

  /**
   * Questions without an own answer get the default of the BE. If a shown answer differs from the saved state, e.g.
   * after a changed selection (saved without answers), a PUT fetches the matching counts.
   */
  private enterConfirm(): void {
    const state = this.importState()!;
    const answers = { ...this.answers() };
    let differs = false;
    for (const q of state.report.questions.filter(isConfirmQuestion)) {
      if (answers[q.id] === undefined && q.defaultAnswer !== null) answers[q.id] = q.defaultAnswer;
      if (answers[q.id] !== undefined && answers[q.id] !== (state.answers?.[q.id] ?? q.defaultAnswer)) differs = true;
    }
    this.answers.set(answers);
    if (differs) this.refreshCounts();
  }

  private showUploadError(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    if (status === 413) {
      this.errorKey.set('classicImport.errors.tooLarge');
    } else if (status === 403) {
      this.errorKey.set('classicImport.errors.forbidden');
    } else if (status === 400) {
      const text = serverMessage(err);
      if (text) this.errorText.set(text);
      else this.errorKey.set('classicImport.errors.invalid');
    } else {
      this.errorKey.set('classicImport.errors.upload');
    }
  }

  // A failed DELETE is not shown: the cleanup job of the BE removes the file later.
  // No takeUntilDestroyed, so the request also goes out when the dialog is destroyed right after closing.
  private discardImport(): void {
    const state = this.importState();
    if (state && state.status !== 'COMMITTED') {
      this.importService.discard(this.projectId(), state.id).subscribe({ error: () => undefined });
    }
    this.upload?.unsubscribe();
    this.upload = null;
    this.refresh?.unsubscribe();
    this.commitRun?.unsubscribe();
    this.cancelRefreshTimer();
    this.refreshing.set(false);
    this.committing.set(false);
    this.commitFailure.set(null);
    this.alreadyCommitted.set(false);
    this.busy.set(false);
    this.importState.set(null);
    this.pendingSelection.set(null);
    this.answers.set({});
    this.step.set(1);
    this.clearError();
  }

  private clearError(): void {
    this.errorKey.set(null);
    this.errorText.set(null);
  }
}

function isBadRequest(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 400;
}

function isUnclear(err: unknown): boolean {
  return !(err instanceof HttpErrorResponse) || err.status === 0 || err.status >= 500;
}

function sameSelection(a: string[] | null, b: string[] | null): boolean {
  if (a === null || b === null) return a === b;
  const set = new Set(b);
  return a.length === set.size && a.every((k) => set.has(k));
}
